/**
 * sc-bg-picker.js — Customily "Choose Background" option truncation
 *
 * Behaviour:
 *  - Detect Customily radio group `properties[Choose Background]` after SDK injects.
 *  - Show first 4 swatches on mobile / 8 on desktop + a "+N MORE" tile.
 *  - Click tile → inline expand (show all).
 *  - If currently selected swatch falls outside the threshold, auto-expand on load
 *    so the user never loses sight of their selection.
 *
 * Dependencies: none. Vanilla JS, IIFE, no build step.
 * Safe to include on non-Customily pages — self-bails if option not found.
 */
(function () {
  'use strict';

  // Stable selector — radio input name is set by Customily based on option label
  var BG_INPUT_SELECTOR = 'input[name="properties[Choose Background]"]';
  var OPTIONS_ROOT = '#cl_optionsapp';
  var SWATCH_SELECTOR = '.customily-swatch';
  var DEFAULT_THRESHOLD_MOBILE = 4;
  var DEFAULT_THRESHOLD_DESKTOP = 8;
  var BREAKPOINT = 768;
  var OBSERVER_TIMEOUT_MS = 15000;

  function getThreshold() {
    var cfg = window.SC_BG_PICKER_CONFIG || {};
    var mobile = parseInt(cfg.thresholdMobile, 10) || DEFAULT_THRESHOLD_MOBILE;
    var desktop = parseInt(cfg.thresholdDesktop, 10) || DEFAULT_THRESHOLD_DESKTOP;
    return window.innerWidth >= BREAKPOINT ? desktop : mobile;
  }

  function getSelectedIndex(swatches) {
    for (var i = 0; i < swatches.length; i++) {
      var input = swatches[i].querySelector('input');
      if (input && input.checked) return i;
    }
    return -1;
  }

  function injectTile(container, hiddenCount, optionEl) {
    // Guard: only inject once
    if (container.querySelector('.sc-bg-more-tile')) return;

    var tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'sc-bg-more-tile';
    tile.textContent = '+' + hiddenCount + ' More';
    tile.setAttribute('aria-label', 'Show ' + hiddenCount + ' more backgrounds');
    tile.addEventListener('click', function () {
      optionEl.removeAttribute('data-sc-collapsed');
    });
    container.appendChild(tile);
  }

  function setupPicker(optionEl) {
    if (optionEl.dataset.scBgPicker === 'ready') return;

    var container = optionEl.querySelector('.swatch-container');
    if (!container) return;

    var swatches = container.querySelectorAll(SWATCH_SELECTOR);

    // Wait for Customily to finish rendering swatches
    if (swatches.length === 0) return;

    var threshold = getThreshold();
    // Not worth truncating if only a couple extra
    if (swatches.length <= threshold + 1) {
      optionEl.dataset.scBgPicker = 'skipped';
      return;
    }

    optionEl.classList.add('sc-bg-picker');
    optionEl.dataset.scBgPicker = 'ready';

    // Auto-expand if selected swatch is beyond threshold
    var selectedIdx = getSelectedIndex(swatches);
    var shouldCollapse = selectedIdx === -1 || selectedIdx < threshold;
    if (shouldCollapse) {
      optionEl.setAttribute('data-sc-collapsed', '');
    }

    var hidden = swatches.length - threshold;
    injectTile(container, hidden, optionEl);
  }

  function findBgOption() {
    var bgInput = document.querySelector(BG_INPUT_SELECTOR);
    if (!bgInput) return null;
    return bgInput.closest('.customily_option');
  }

  function trySetup() {
    var opt = findBgOption();
    if (!opt) return false;
    var flag = opt.dataset.scBgPicker;
    if (flag === 'ready' || flag === 'skipped') return true;
    setupPicker(opt);
    flag = opt.dataset.scBgPicker;
    return flag === 'ready' || flag === 'skipped';
  }

  function init() {
    // Try immediately — if Customily already rendered with swatches
    if (trySetup()) return;

    // Observe document body — `#cl_optionsapp` may not exist yet at script
    // load time (Customily SDK mounts it asynchronously), so we can't target
    // it directly. Body is guaranteed to exist.
    var observer = new MutationObserver(function () {
      if (trySetup()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Safety: disconnect after timeout regardless
    setTimeout(function () { observer.disconnect(); }, OBSERVER_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
